import React, { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { Users as UsersIcon, Plus, Pencil, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLE_OPTIONS = ["it_admin", "direktur", "approver", "tim_keuangan", "pemohon", "staf_tagihan", "tim_kendaraan"];
const BRANCH_REQUIRED_ROLES = ["pemohon", "approver", "tim_kendaraan"];
const ROLE_COLORS = {
  it_admin: "bg-slate-800 text-white",
  direktur: "bg-emerald-700 text-white",
  approver: "bg-sky-600 text-white",
  tim_keuangan: "bg-amber-500 text-white",
  pemohon: "bg-slate-200 text-slate-700",
  staf_tagihan: "bg-purple-600 text-white",
  tim_kendaraan: "bg-teal-600 text-white",
};

const EMPTY = { name: "", email: "", password: "", role: "pemohon", divisi: "", branch_id: "", approver_all_branches: true, company_id: "" };

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user?.role === "it_admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setError(""); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "", branch_id: u.branch_id || "", company_id: u.company_id || "" }); setError(""); setOpen(true); };

  const branchRequired = BRANCH_REQUIRED_ROLES.includes(form.role);

  const save = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, divisi: form.divisi, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        if (BRANCH_REQUIRED_ROLES.includes(form.role)) payload.branch_id = form.branch_id;
        if (form.role === "approver") payload.approver_all_branches = !!form.approver_all_branches;
        if (["pemohon", "tim_kendaraan"].includes(form.role)) payload.company_id = form.company_id || "";
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User diperbarui.");
      } else {
        await api.post("/users", form);
        toast.success("User baru terdaftar.");
      }
      setOpen(false); load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${delTarget.id}`);
      toast.success("User dihapus.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setDelTarget(null);
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (err) { toast.error("Gagal mengubah status"); }
  };

  const toggleSales = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { sales_enabled: !u.sales_enabled });
      toast.success(`Input Transaksi ${!u.sales_enabled ? "diaktifkan" : "dinonaktifkan"} untuk ${u.name}.`);
      load();
    } catch (err) { toast.error("Gagal mengubah akses"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <UsersIcon className="h-6 w-6 text-brand" /> Manajemen User
          </h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Daftarkan user & atur role serta divisi." : "Daftar user sistem (hanya lihat)."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-brand hover:bg-emerald-800" data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" /> Tambah User
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Cabang</TableHead>
                <TableHead>Divisi</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead>Input Transaksi</TableHead>}
                {isAdmin && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={`${ROLE_COLORS[u.role]} border-0`}>{ROLE_LABELS[u.role]}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{u.branch_name || "-"}{u.role === "pemohon" && u.company_name && <span className="block text-xs text-brand" data-testid={`user-company-${u.id}`}>{u.company_name}</span>}</TableCell>
                  <TableCell className="text-sm">{u.divisi || "-"}</TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}
                        className={`text-xs font-medium px-2 py-1 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                        {u.is_active ? "Aktif" : "Nonaktif"}
                      </button>
                    ) : (
                      <span className={`text-xs ${u.is_active ? "text-emerald-700" : "text-slate-500"}`}>{u.is_active ? "Aktif" : "Nonaktif"}</span>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {u.role === "pemohon" ? (
                        <label className="inline-flex items-center gap-2 cursor-pointer" title="Akses menu Input Transaksi, Piutang & Setor Kas">
                          <Switch checked={!!u.sales_enabled} onCheckedChange={() => toggleSales(u)} data-testid={`toggle-sales-${u.id}`} />
                          <span className={`text-xs ${u.sales_enabled ? "text-emerald-700 font-medium" : "text-muted-foreground"}`}>{u.sales_enabled ? "Aktif" : "Nonaktif"}</span>
                        </label>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setDelTarget(u)} data-testid={`delete-user-${u.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Tambah User Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="uname">Nama Lengkap</Label>
              <Input id="uname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="user-name-input" />
            </div>
            <div>
              <Label htmlFor="uemail">Email</Label>
              <Input id="uemail" type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="user-email-input" />
            </div>
            <div>
              <Label htmlFor="upwd">{editing ? "Password Baru (opsional)" : "Password"}</Label>
              <Input id="upwd" type="text" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing}
                className="mt-1.5" data-testid="user-password-input" placeholder={editing ? "Kosongkan jika tidak diubah" : ""} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="udiv">Divisi</Label>
                <Input id="udiv" value={form.divisi} onChange={(e) => setForm({ ...form, divisi: e.target.value })} className="mt-1.5" data-testid="user-divisi-input" />
              </div>
            </div>
            {branchRequired && (
              <div>
                <Label>Cabang <span className="text-red-500">*</span></Label>
                <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-branch-select"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Wajib untuk role Pemohon, Approver, dan Tim Kendaraan.</p>
              </div>
            )}
            {["pemohon", "tim_kendaraan"].includes(form.role) && (
              <div>
                <Label>{form.role === "pemohon" ? "PT Pemohon" : "PT Tim Kendaraan"}</Label>
                <select value={form.company_id || ""} onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                  className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="user-company-select">
                  <option value="">Belum ditentukan</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Dipakai otomatis sebagai PT sumber dana saat pencairan kas{form.role === "pemohon" ? "; penjualan Cash Pemohon ini dihitung sebagai pendapatan PT tersebut" : ""}.</p>
              </div>
            )}
            {form.role === "approver" && (
              <div className="flex items-start gap-2.5 rounded-lg border p-3 bg-secondary/40">
                <Checkbox id="allbr" checked={!!form.approver_all_branches}
                  onCheckedChange={(v) => setForm({ ...form, approver_all_branches: !!v })}
                  className="mt-0.5" data-testid="approver-all-branches-check" />
                <div>
                  <Label htmlFor="allbr" className="cursor-pointer">Setujui semua cabang</Label>
                  <p className="text-xs text-muted-foreground">Jika aktif, approver ini melihat & menyetujui pengajuan dari seluruh cabang. Jika nonaktif, hanya cabang yang dipilih di atas.</p>
                </div>
              </div>
            )}
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="user-form-error">{error}</p>}
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-user-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Simpan Perubahan" : "Daftarkan User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus user?</AlertDialogTitle>
            <AlertDialogDescription>
              User <strong>{delTarget?.name}</strong> ({delTarget?.email}) akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
